import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { humanize } from 'underscore.string';
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableBody,
  TableFooter,
  TableCell,
} from 'components/ui/table';
import Link from 'components/v2/Link';
import ExternalLinkIcon from 'components/v2/icons/ExternalLinkIcon';
import { isIntermodalShipmentType } from 'helpers/shipment-types';
import { withTranslation } from 'react-i18next';
import { sluggify } from 'utils/localization/utils';
import formatMoney from 'utils/formatters/Money';
import { AuthorizationWrapper } from 'components/wrappers/AuthorizationWrapper';
import { REGIONS } from 'constants/Regions';

class CostSummary extends Component {
  getTotal() {
    const {
      isShipper,
      job: { payables, receivables },
    } = this.props;
    const ledger = isShipper ? receivables : payables;
    return ledger.reduce((acc, item) => acc + (item.value || 0), 0);
  }

  renderTotal() {
    const {
      t,
      isShipper,
      job: { currency },
    } = this.props;
    return (
      <TableRow>
        <TableCell>&nbsp;</TableCell>
        {!isShipper && <TableCell>&nbsp;</TableCell>}
        <TableCell className="font-bold">{t('TOTAL')}</TableCell>
        <TableCell className="text-right font-bold">
          {formatMoney(this.getTotal(), currency)}
        </TableCell>
      </TableRow>
    );
  }

  renderLineItems(accountingDoc, unprocessed = false) {
    const {
      t,
      isShipper,
      job: {
        external_payables,
        external_receivables,
        shipment_type,
        currency,
        receivables,
        payables,
      },
    } = this.props;
    let lineItems = [];
    const isDrayage = isIntermodalShipmentType(shipment_type);
    if (!unprocessed) {
      lineItems = isShipper
        ? receivables.filter(
            (lineItem) => lineItem.invoice_id === accountingDoc._id,
          )
        : payables.filter(
            (lineItem) => lineItem.payout_id === accountingDoc._id,
          );
    }
    if (unprocessed) {
      lineItems = isShipper
        ? receivables.filter(
            (lineItem) => !lineItem.invoice_id || !lineItem.processed,
          )
        : payables.filter(
            (lineItem) => !lineItem.payout_id || !lineItem.processed,
          );
    }
    const costType = isShipper ? 'invoices' : 'carrier-payouts';
    return (
      <>
        {lineItems.map((lineItem) => (
          <TableRow>
            <TableCell>
              {accountingDoc && (
                <Link url={`/${costType}/${accountingDoc._id}`}>
                  <div className="flex align-middle items-center space-x-1">
                    <p>{accountingDoc.number}</p>
                    <ExternalLinkIcon className="w-4 h-4" />
                  </div>
                </Link>
              )}
            </TableCell>
            {!isShipper && (
              <TableCell>
                {accountingDoc && t(sluggify(accountingDoc.status))}
              </TableCell>
            )}
            <TableCell>{t(sluggify(lineItem.message))}</TableCell>
            <TableCell className="text-right">
              {formatMoney(lineItem.value, currency)}
            </TableCell>
          </TableRow>
        ))}
      </>
    );
  }

  render() {
    const { isShipper, t, accountingDocs } = this.props;
    const label = isShipper
      ? t('INVOICE', { count: 1 })
      : t('PAYOUT', { count: 1 });

    return (
      <div className="job_details__payout">
        <h4 className="mb-2 text-grey-darker">{`${label} ${t('SUMMARY')}`}</h4>
        <AuthorizationWrapper excludedRegions={[REGIONS.AP]}>
          <div className="table-wrapper my-4">
            <Table className="">
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead sortKey="number">{`${label} ${t(
                    'NUMBER',
                  )}`}</TableHead>
                  {!isShipper && (
                    <TableHead sortKey="status">{t('STATUS')}</TableHead>
                  )}
                  <TableHead sortKey="charge">{t('CHARGE')}</TableHead>
                  <TableHead sortKey="amount" className="text-right">
                    {t('AMOUNT')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {accountingDocs.map((accountingDoc) =>
                  this.renderLineItems(accountingDoc, false),
                )}
                {this.renderLineItems(null, true)}
                {this.renderTotal(null, true)}
              </TableBody>
            </Table>
          </div>
        </AuthorizationWrapper>
      </div>
    );
  }
}

CostSummary.propTypes = {
  isShipper: PropTypes.bool,
  job: PropTypes.shape({
    external_payables: PropTypes.arrayOf(
      PropTypes.shape({
        message: PropTypes.string,
        type: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired,
      }),
    ),
    external_receivables: PropTypes.arrayOf(
      PropTypes.shape({
        message: PropTypes.string,
        type: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired,
      }),
    ),
  }),
  accountingDocs: PropTypes.arrayOf(
    PropTypes.shape({
      number: PropTypes.string,
      status: PropTypes.string,
    }),
  ).isRequired,
};

CostSummary.defaultProps = {
  job: {
    payables: [],
  },
  isShipper: false,
};

export default withTranslation()(CostSummary);
